import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Users,
  Search,
  Plus,
  Phone,
  ClipboardList,
  PhoneCall,
  MessageCircle,
  Download,
  Trash2,
  Save,
} from "lucide-react";

const IST_DATE = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const RELATIVE = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.floor(d / 30)} mo ago`;
  return `${Math.floor(d / 365)} yr ago`;
};

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });

  const [detail, setDetail] = useState(null); // {customer, tasks, calls} | null
  const [detailForm, setDetailForm] = useState({ name: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/customers?limit=500${q ? `&q=${encodeURIComponent(q)}` : ""}`
      );
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const total = items.length;
    const withCalls = items.filter((c) => (c.call_count || 0) > 0).length;
    const recent = items.filter((c) => {
      if (!c.last_contacted_at) return false;
      return Date.now() - new Date(c.last_contacted_at).getTime() < 7 * 86400000;
    }).length;
    return { total, withCalls, recent };
  }, [items]);

  const createCustomer = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      await api.post("/customers", form);
      toast.success("Customer saved");
      setCreating(false);
      setForm({ name: "", phone: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const openDetail = async (c) => {
    setDetail({ customer: c, tasks: [], calls: [] });
    setDetailForm({ name: c.name || "", notes: c.notes || "" });
    try {
      const { data } = await api.get(`/customers/${c.id}`);
      setDetail(data);
      setDetailForm({
        name: data.customer.name || "",
        notes: data.customer.notes || "",
      });
    } catch (e) {
      toast.error("Could not load customer details");
    }
  };

  const saveDetail = async () => {
    if (!detail) return;
    try {
      await api.patch(`/customers/${detail.customer.id}`, detailForm);
      toast.success("Customer updated");
      setDetail(null);
      await load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const removeCustomer = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete ${detail.customer.name}? Their tasks and calls will remain, but the contact is removed.`)) return;
    try {
      await api.delete(`/customers/${detail.customer.id}`);
      toast.success("Customer removed");
      setDetail(null);
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const call = async (c) => {
    try {
      const { data } = await api.post("/calls/initiate", {
        customer_phone: c.phone,
        customer_name: c.name,
      });
      toast.success(data.message || "Your phone will ring shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Call failed");
    }
  };

  const exportCsv = () => {
    if (!items.length) return;
    const rows = [
      [
        "Name",
        "Phone",
        "Tasks",
        "Calls",
        "First added",
        "Last contacted",
        "Notes",
      ],
      ...items.map((c) => [
        c.name || "",
        c.phone || "",
        c.task_count || 0,
        c.call_count || 0,
        c.first_added_at || "",
        c.last_contacted_at || "",
        c.notes || "",
      ]),
    ];
    const csv = rows
      .map((r) =>
        r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dialflow-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-customers">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Customer database
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">
            Everyone you&apos;ve talked to
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Every task or call automatically adds the customer here. Search,
            re-call them, or send a WhatsApp any time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={!items.length}
            data-testid="customers-export"
          >
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button
            onClick={() => setCreating(true)}
            className="bg-[#0055FF] hover:bg-[#0040CC]"
            data-testid="customers-new"
          >
            <Plus className="w-4 h-4 mr-1" /> Add customer
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="mono text-[10px] uppercase text-slate-500">
              Total contacts
            </div>
            <div className="font-heading text-3xl font-bold mt-1">
              {stats.total}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="mono text-[10px] uppercase text-slate-500">
              With call history
            </div>
            <div className="font-heading text-3xl font-bold mt-1">
              {stats.withCalls}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="mono text-[10px] uppercase text-slate-500">
              Contacted last 7 days
            </div>
            <div className="font-heading text-3xl font-bold mt-1 text-[#FF5000]">
              {stats.recent}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b flex-row items-center justify-between space-y-0 flex-wrap gap-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-[#0055FF]" />
            All customers
          </CardTitle>
          <div className="relative w-full max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or phone…"
              className="pl-9"
              data-testid="customers-search"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              No customers yet. Assign a task to any customer and they&apos;ll
              show up here automatically.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Phone</th>
                  <th className="text-left px-5 py-3">Tasks</th>
                  <th className="text-left px-5 py-3">Calls</th>
                  <th className="text-left px-5 py-3">Last contacted</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-slate-50"
                    data-testid={`customer-row-${c.id}`}
                  >
                    <td
                      className="px-5 py-3 cursor-pointer"
                      onClick={() => openDetail(c)}
                    >
                      <div className="font-medium">{c.name || "Unknown"}</div>
                      {c.notes && (
                        <div className="mono text-[10px] text-slate-500 truncate max-w-[300px]">
                          {c.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 mono text-xs text-slate-700">
                      {c.phone}
                    </td>
                    <td className="px-5 py-3 mono text-xs text-slate-700">
                      {c.task_count || 0}
                    </td>
                    <td className="px-5 py-3 mono text-xs text-slate-700">
                      {c.call_count || 0}
                    </td>
                    <td className="px-5 py-3 mono text-xs text-slate-600">
                      <div>{IST_DATE(c.last_contacted_at)}</div>
                      <div className="text-slate-400 text-[10px]">
                        {RELATIVE(c.last_contacted_at)}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => call(c)}
                        className="text-[#0055FF] hover:bg-blue-50"
                        title="Call this customer"
                        data-testid={`customer-call-${c.id}`}
                      >
                        <PhoneCall className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDetail(c)}
                        className="text-slate-500"
                        data-testid={`customer-view-${c.id}`}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Add customer dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="customers-create-dialog">
          <DialogHeader>
            <DialogTitle>Add customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Customer name"
                data-testid="customers-create-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91…"
                data-testid="customers-create-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Prefers evening calls"
                data-testid="customers-create-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              onClick={createCustomer}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="customers-create-submit"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet — right-side drawer with tasks + calls history */}
      <Sheet
        open={!!detail}
        onOpenChange={(v) => !v && setDetail(null)}
      >
        <SheetContent
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="customer-detail"
        >
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-2xl">
                  {detail.customer.name || "Unknown"}
                </SheetTitle>
                <div className="mono text-sm text-slate-500 flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" /> {detail.customer.phone}
                </div>
              </SheetHeader>

              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="border rounded p-2 text-center">
                  <div className="mono text-[9px] uppercase text-slate-500">
                    Tasks
                  </div>
                  <div className="font-heading text-lg font-bold">
                    {detail.customer.task_count || 0}
                  </div>
                </div>
                <div className="border rounded p-2 text-center">
                  <div className="mono text-[9px] uppercase text-slate-500">
                    Calls
                  </div>
                  <div className="font-heading text-lg font-bold">
                    {detail.customer.call_count || 0}
                  </div>
                </div>
                <div className="border rounded p-2 text-center">
                  <div className="mono text-[9px] uppercase text-slate-500">
                    Since
                  </div>
                  <div className="font-heading text-xs font-bold mt-1">
                    {IST_DATE(detail.customer.first_added_at)}
                  </div>
                </div>
              </div>

              <div className="space-y-3 mt-6">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={detailForm.name}
                    onChange={(e) =>
                      setDetailForm({ ...detailForm, name: e.target.value })
                    }
                    data-testid="customer-edit-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    rows={3}
                    value={detailForm.notes}
                    onChange={(e) =>
                      setDetailForm({ ...detailForm, notes: e.target.value })
                    }
                    placeholder="Anything worth remembering about this customer"
                    data-testid="customer-edit-notes"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={saveDetail}
                    className="bg-[#0055FF] hover:bg-[#0040CC]"
                    data-testid="customer-edit-save"
                  >
                    <Save className="w-4 h-4 mr-1" /> Save
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => call(detail.customer)}
                    className="text-[#0055FF]"
                    data-testid="customer-detail-call"
                  >
                    <PhoneCall className="w-4 h-4 mr-1" /> Call now
                  </Button>
                  <Button
                    variant="outline"
                    onClick={removeCustomer}
                    className="text-red-600 hover:text-red-700 ml-auto"
                    data-testid="customer-delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {detail.tasks?.length > 0 && (
                <div className="mt-8">
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                    <ClipboardList className="w-3 h-3" /> Task history (
                    {detail.tasks.length})
                  </div>
                  <div className="space-y-2">
                    {detail.tasks.map((t) => (
                      <div
                        key={t.id}
                        className="border rounded p-2 text-xs"
                        data-testid={`customer-task-${t.id}`}
                      >
                        <div className="font-medium">{t.title}</div>
                        <div className="mono text-[10px] text-slate-500 mt-0.5">
                          {t.status} · {IST_DATE(t.created_at)}
                        </div>
                        {t.done_comment && (
                          <div className="mono text-[10px] text-emerald-700 mt-1">
                            Outcome: {t.done_comment}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.calls?.length > 0 && (
                <div className="mt-6">
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> Call history (
                    {detail.calls.length})
                  </div>
                  <div className="space-y-2">
                    {detail.calls.map((c) => (
                      <div
                        key={c.id}
                        className="border rounded p-2 text-xs"
                        data-testid={`customer-call-${c.id}`}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium">
                            {c.employee_name || "—"}
                          </span>
                          <span className="mono text-[10px] text-slate-500">
                            {IST_DATE(c.started_at)}
                          </span>
                        </div>
                        <div className="mono text-[10px] text-slate-500 mt-0.5">
                          {c.duration_sec}s · {c.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
